import { StorageEmulator } from './StorageEmulator'

/**
 * A wrapper class for the Storage API that uses StorageEmulator as a fallback.
 */
export class StorageWrapper {
  private storage: StorageEmulator

  /**
   * Creates an instance of StorageWrapper.
   * @param {Storage} [customStorage] - An optional custom storage object to use.
   */
  constructor(customStorage?: Storage) {
    this.storage = new StorageEmulator(customStorage)
  }

  /**
   * Sets a key-value pair in the storage.
   * @param {string} key - The key to set.
   * @param {string} value - The value to associate with the key.
   */
  setItem(key: string, value: string): void {
    this.storage.setItem(key, value)
  }

  /**
   * Retrieves the value associated with the given key.
   * @param {string} key - The key to retrieve.
   * @returns {string | null} The value associated with the key, or null if not found.
   */
  getItem(key: string): string | null {
    return this.storage.getItem(key)
  }

  /**
   * Removes the key-value pair associated with the given key.
   * @param {string} key - The key to remove.
   */
  removeItem(key: string): void {
    this.storage.removeItem(key)
  }

  /**
   * Clears all key-value pairs in the storage.
   */
  clear(): void {
    this.storage.clear()
  }

  /**
   * Gets the number of key-value pairs in the storage.
   * @returns {number} The number of items in the storage.
   */
  get length(): number {
    return this.storage.length
  }

  /**
   * Retrieves the key at the specified index.
   * @param {number} index - The index of the key to retrieve.
   * @returns {string | null} The key at the specified index, or null if not found.
   */
  key(index: number): string | null {
    return this.storage.key(index)
  }
}
